import React from 'react';

// Match the JS errors the browser throws when a code-split chunk fails
// to load — typically because the PWA service worker has a stale
// hashed filename in its cache after a deploy.
const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes('importing a module script failed')
  );
};

async function hardReloadOnce() {
  try {
    const key = 'baremc.hardReload.app';
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, String(Date.now()));
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch (_e) { /* ignore */ }
  window.location.reload();
}

// Global catch for chunk-load errors that bypass React (e.g. during
// router suspense, idle prefetch, or async dynamic import outside a
// boundary). One listener at app start auto-recovers them all.
if (typeof window !== 'undefined' && !window.__baremcChunkGuard) {
  window.__baremcChunkGuard = true;
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event?.error)) {
      hardReloadOnce();
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event?.reason)) {
      hardReloadOnce();
    }
  });
}

// NOTE: ErrorBoundary is a class component and cannot use React hooks.
// Strings here are intentionally English fallbacks since this UI only renders
// when the entire app has crashed (potentially including the i18n context itself).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
    // Persist the real error so it can be inspected after the fact (and
    // screenshotted by the user) — critical for diagnosing intermittent
    // production crashes that we cannot reproduce in preview.
    try {
      localStorage.setItem('baremc_last_error', JSON.stringify({
        at: new Date().toISOString(),
        name: error?.name || null,
        message: error?.message || String(error),
        chunk: isChunkLoadError(error),
        stack: (error?.stack || '').slice(0, 800),
        component: (errorInfo?.componentStack || '').slice(0, 500),
        url: window.location.href,
      }));
    } catch (_e) { /* ignore */ }
    if (isChunkLoadError(error)) {
      this.setState({ recovering: true });
      hardReloadOnce();
    }
  }

  // Manual recovery: ALWAYS purge the service worker + all caches, then
  // reload from the network. A plain reload can re-use a stale SW/HTTP cache
  // and land the user right back on the error page.
  clearAndReload = async (goHome) => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
      }
      sessionStorage.removeItem('baremc.hardReload.app');
    } catch (_e) { /* ignore */ }
    if (goHome) {
      window.location.replace('/');
    } else {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback was provided (e.g. a small error strip), use it.
      // fallback={null} = silent failure (nothing rendered); fallback={<jsx>} = custom UI.
      if ('fallback' in this.props) {
        return this.props.fallback;
      }
      if (this.state.recovering) {
        return (
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0c1611',
            color: '#d1d5db',
            fontFamily: 'system-ui, sans-serif',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 28, height: 28, border: '3px solid rgba(45,255,136,0.25)', borderTopColor: '#2DFF88',
                borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 12px',
              }} />
              <p style={{ fontSize: 14 }}>Opdaterer app…</p>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        );
      }
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a1412',
          color: '#fff',
          fontFamily: 'system-ui, sans-serif',
          padding: '20px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '12px', color: '#4ade80' }}>Noget gik galt</h2>
            <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
              Der opstod en uventet fejl. Genindlæs siden eller gå til forsiden.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => this.clearAndReload(true)}
                style={{
                  background: 'transparent',
                  color: '#4ade80',
                  border: '1px solid #4ade80',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                data-testid="error-boundary-home"
              >
                Gå til forsiden
              </button>
              <button
                onClick={() => this.clearAndReload(false)}
                style={{
                  background: '#4ade80',
                  color: '#0a1412',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                data-testid="error-boundary-reload"
              >
                Genindlæs
              </button>
            </div>
            {this.state.error && (
              <p style={{ color: '#6b7280', marginTop: '18px', fontSize: '11px', wordBreak: 'break-word', lineHeight: 1.5 }} data-testid="error-boundary-detail">
                {(this.state.error.name ? this.state.error.name + ': ' : '') + (this.state.error.message || String(this.state.error))}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
